import { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { motion, useScroll, useTransform, useInView, AnimatePresence } from 'framer-motion';
import { getProducts, getCollections } from '../firebase/database';
import ProductCard from '../components/shop/ProductCard';
import SEO from '../components/common/SEO';
import { SkeletonCard } from '../components/common/Loader';
import { formatPrice } from '../utils/helpers';
import ContactCTA from '../components/common/ContactCTA';

const FadeUp = ({ children, delay = 0, className = '' }) => {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: '-80px' });
  return (
    <motion.div ref={ref} initial={{ opacity: 0, y: 50 }}
      animate={inView ? { opacity: 1, y: 0 } : {}}
      transition={{ duration: 0.75, delay, ease: [0.22, 1, 0.36, 1] }}
      className={className}>{children}</motion.div>
  );
};
const FadeLeft = ({ children, delay = 0, className = '' }) => {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: '-80px' });
  return (
    <motion.div ref={ref} initial={{ opacity: 0, x: -30 }}
      animate={inView ? { opacity: 1, x: 0 } : {}}
      transition={{ duration: 0.7, delay, ease: [0.22, 1, 0.36, 1] }}
      className={className}>{children}</motion.div>
  );
};
const FadeRight = ({ children, delay = 0, className = '' }) => {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: '-80px' });
  return (
    <motion.div ref={ref} initial={{ opacity: 0, x: 30 }}
      animate={inView ? { opacity: 1, x: 0 } : {}}
      transition={{ duration: 0.7, delay, ease: [0.22, 1, 0.36, 1] }}
      className={className}>{children}</motion.div>
  );
};

// ─── HERO — built from real products, not stock photos ───────────────────────
const Hero = ({ heroProducts, loading }) => {
  const [slide, setSlide] = useState(0);
  const { scrollY } = useScroll();
  const bgY = useTransform(scrollY, [0, 700], [0, 200]);
  const contentOpacity = useTransform(scrollY, [0, 400], [1, 0]);
  const contentY = useTransform(scrollY, [0, 400], [0, -60]);

  const bgGradients = [
    'from-[#020d1f] via-[#0a1e3d] to-[#1a3a6e]',
    'from-[#1a0508] via-[#3a0c12] to-[#6b1520]',
    'from-[#020f1d] via-[#0b2040] to-[#173669]',
  ];

  useEffect(() => {
    if (heroProducts.length < 2) return;
    const t = setInterval(() => setSlide(s => (s + 1) % heroProducts.length), 5500);
    return () => clearInterval(t);
  }, [heroProducts.length]);

  if (loading) {
    return (
      <section className="relative min-h-screen flex items-center justify-center bg-[#0f2347]">
        <div className="w-10 h-10 border-2 border-white/20 border-t-white rounded-full animate-spin" />
      </section>
    );
  }

  if (heroProducts.length === 0) {
    return (
      <section className="relative min-h-[60vh] flex items-center justify-center bg-gradient-to-br from-[#020d1f] via-[#0a1e3d] to-[#1a3a6e]">
        <div className="text-center px-4">
          <h1 className="font-display text-4xl md:text-6xl font-black text-white mb-4">
            <span className="text-[#e63946]">Sit</span>Smart
          </h1>
          <p className="text-white/60 mb-8">Add products from the admin panel to bring this page to life.</p>
          <Link to="/shop" className="inline-flex items-center gap-2 bg-[#e63946] text-white px-8 py-4 font-black text-sm hover:bg-white hover:text-[#e63946] transition-all duration-300">
            Browse Shop
          </Link>
        </div>
      </section>
    );
  }

  const p = heroProducts[slide];
  const price = p.salePrice && p.salePrice < p.price ? p.salePrice : p.price;

  return (
    <section className="relative min-h-screen flex items-center overflow-hidden">
      <AnimatePresence mode="wait">
        <motion.div key={`bg-${slide}`} initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
          transition={{ duration: 0.9 }} className={`absolute inset-0 bg-gradient-to-br ${bgGradients[slide % bgGradients.length]}`} />
      </AnimatePresence>
      <motion.div style={{ y: bgY }} className="absolute inset-0 pointer-events-none">
        <AnimatePresence mode="wait">
          <motion.img key={`img-${slide}`} src={p.images?.[0]} alt=""
            initial={{ opacity: 0, scale: 1.08 }} animate={{ opacity: 0.18, scale: 1 }} exit={{ opacity: 0 }}
            transition={{ duration: 1.3 }} className="w-full h-full object-cover"
            onError={e => e.target.style.display = 'none'} />
        </AnimatePresence>
      </motion.div>
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <motion.div animate={{ rotate: 360 }} transition={{ duration: 40, repeat: Infinity, ease: 'linear' }}
          className="absolute -top-48 -right-48 w-[500px] h-[500px] border border-white/[0.04] rounded-full" />
        <motion.div animate={{ rotate: -360 }} transition={{ duration: 28, repeat: Infinity, ease: 'linear' }}
          className="absolute -top-24 -right-24 w-80 h-80 border border-white/[0.06] rounded-full" />
        {[...Array(8)].map((_, i) => (
          <motion.div key={i} className="absolute w-1 h-1 bg-white/30 rounded-full"
            style={{ left: `${10 + i * 12}%`, top: `${15 + (i % 4) * 20}%` }}
            animate={{ y: [0, -18, 0], opacity: [0.15, 0.5, 0.15] }}
            transition={{ duration: 3 + i * 0.6, repeat: Infinity, delay: i * 0.4 }} />
        ))}
        <motion.div animate={{ scale: [1, 1.15, 1], opacity: [0.06, 0.12, 0.06] }}
          transition={{ duration: 7, repeat: Infinity }}
          className="absolute top-1/3 right-1/4 w-96 h-96 bg-[#e63946] rounded-full blur-3xl" />
      </div>
      <motion.div style={{ opacity: contentOpacity, y: contentY }}
        className="relative max-w-7xl mx-auto px-4 sm:px-6 w-full pt-24 pb-20">
        <div className="grid lg:grid-cols-2 gap-10 items-center">
          <div>
            <AnimatePresence mode="wait">
              <motion.div key={`content-${slide}`} initial={{ opacity: 0, y: 50 }}
                animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -30 }} transition={{ duration: 0.65 }}>
                <div className="inline-flex items-center gap-2 mb-7"
                  style={{ background: 'rgba(255,255,255,0.1)', backdropFilter: 'blur(12px)', border: '1px solid rgba(255,255,255,0.18)', padding: '8px 18px' }}>
                  <motion.span animate={{ scale: [1, 1.4, 1] }} transition={{ duration: 1.5, repeat: Infinity }}
                    className="w-2 h-2 bg-[#e63946] rounded-full block" />
                  <span className="text-white/90 text-xs font-black uppercase tracking-[0.25em]">{p.isNew ? 'New Arrival' : 'Featured Product'}</span>
                </div>
                <h1 className="font-display text-[clamp(2.5rem,7vw,5.5rem)] font-black text-white leading-[0.95] tracking-tight mb-7 line-clamp-2">{p.title}</h1>
                <p className="text-white/60 text-lg leading-relaxed mb-9 max-w-[440px] line-clamp-3">{p.shortDescription || p.description}</p>
                <div className="flex flex-wrap gap-4 mb-12">
                  <Link to={`/product/${p.id}`} className="group inline-flex items-center gap-3 bg-[#e63946] text-white px-8 py-4 font-black text-sm hover:bg-white hover:text-[#e63946] transition-all duration-300">
                    Shop This
                    <svg className="w-4 h-4 group-hover:translate-x-1.5 transition-transform" fill="none" stroke="currentColor" strokeWidth="2.5" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M17 8l4 4m0 0l-4 4m4-4H3"/></svg>
                  </Link>
                  <Link to="/collections" className="inline-flex items-center gap-3 text-white font-bold text-sm px-8 py-4 transition-all duration-300"
                    style={{ background: 'rgba(255,255,255,0.1)', backdropFilter: 'blur(10px)', border: '1px solid rgba(255,255,255,0.2)' }}>
                    <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24"><path d="M8 5v14l11-7z"/></svg>
                    View Collections
                  </Link>
                </div>
                <div className="flex gap-8 border-t border-white/10 pt-8">
                  {[{ v: formatPrice(price), l: 'Price' }, { v: 'COD', l: 'Cash on Delivery' }, { v: p.stock > 0 ? 'In Stock' : 'Sold Out', l: 'Availability' }].map(({ v, l }) => (
                    <div key={l}><div className="text-xl font-black text-white">{v}</div>
                      <div className="text-white/40 text-[11px] uppercase tracking-widest mt-0.5">{l}</div></div>
                  ))}
                </div>
              </motion.div>
            </AnimatePresence>
          </div>
          <div className="hidden lg:flex items-center justify-center relative">
            <AnimatePresence mode="wait">
              <motion.div key={`showcase-${slide}`}
                initial={{ opacity: 0, x: 80 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -40 }}
                transition={{ duration: 0.9, ease: [0.22, 1, 0.36, 1] }} className="relative w-[400px] h-[480px]">
                <Link to={`/product/${p.id}`} className="w-full h-full overflow-hidden block bg-white/5">
                  <img src={p.images?.[0]} alt={p.title} className="w-full h-full object-contain p-6"
                    onError={e => e.target.src = 'https://via.placeholder.com/400x480?text=SitSmart'} />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/30 to-transparent pointer-events-none" />
                </Link>
                <motion.div animate={{ y: [0, -10, 0] }} transition={{ duration: 3.5, repeat: Infinity }}
                  className="absolute -left-12 bottom-14 shadow-2xl p-4 w-44"
                  style={{ background: 'rgba(15,35,71,0.92)', backdropFilter: 'blur(16px)', border: '1px solid rgba(255,255,255,0.12)' }}>
                  <p className="text-white/50 text-[10px] uppercase tracking-widest">Price</p>
                  <p className="text-white font-black text-2xl mt-0.5">{formatPrice(price)}</p>
                  <p className="text-[#4ade80] text-[11px] font-semibold mt-1.5 flex items-center gap-1">
                    <svg className="w-3 h-3" fill="none" stroke="currentColor" strokeWidth="3" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7"/></svg>
                    Free Delivery Over 5,000
                  </p>
                </motion.div>
                {p.rating > 0 && (
                  <motion.div animate={{ y: [0, 10, 0] }} transition={{ duration: 4, repeat: Infinity, delay: 1.2 }}
                    className="absolute -right-10 top-10 shadow-2xl p-3.5 text-center"
                    style={{ background: 'rgba(230,57,70,0.92)', backdropFilter: 'blur(16px)' }}>
                    <div className="text-white font-black text-2xl">{p.rating.toFixed(1)}</div>
                    <div className="flex justify-center gap-0.5 my-1">
                      {Array(5).fill(0).map((_, i) => (
                        <svg key={i} className="w-3 h-3 text-yellow-300" fill="currentColor" viewBox="0 0 24 24"><path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z"/></svg>
                      ))}
                    </div>
                    <p className="text-white/80 text-[10px] font-semibold">{p.reviewCount || 0} Reviews</p>
                  </motion.div>
                )}
              </motion.div>
            </AnimatePresence>
          </div>
        </div>
      </motion.div>
      {heroProducts.length > 1 && (
        <div className="absolute bottom-10 left-1/2 -translate-x-1/2 flex items-center gap-3 z-10">
          <button onClick={() => setSlide(s => (s - 1 + heroProducts.length) % heroProducts.length)}
            className="w-9 h-9 flex items-center justify-center text-white/60 hover:text-white border border-white/20 hover:border-white/50 transition-all rounded-full">
            <svg className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth="2.5" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M15 19l-7-7 7-7"/></svg>
          </button>
          {heroProducts.map((_, i) => (
            <button key={i} onClick={() => setSlide(i)}
              className={`rounded-full transition-all duration-500 ${i === slide ? 'bg-[#e63946] w-8 h-2' : 'bg-white/30 w-2 h-2 hover:bg-white/60'}`} />
          ))}
          <button onClick={() => setSlide(s => (s + 1) % heroProducts.length)}
            className="w-9 h-9 flex items-center justify-center text-white/60 hover:text-white border border-white/20 hover:border-white/50 transition-all rounded-full">
            <svg className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth="2.5" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7"/></svg>
          </button>
        </div>
      )}
      <div className="absolute bottom-0 left-0 right-0 pointer-events-none">
        <svg viewBox="0 0 1440 70" fill="none" xmlns="http://www.w3.org/2000/svg" preserveAspectRatio="none">
          <path d="M0 70L1440 70L1440 30C1080 70 720 0 360 40C180 58 90 70 0 35L0 70Z" fill="white"/>
        </svg>
      </div>
    </section>
  );
};

// ─── MARQUEE ──────────────────────────────────────────────────────────────────
const MarqueeBar = () => {
  const items = ['Free Delivery Over PKR 5,000','Premium Quality','1-Year Warranty',"Pakistan's #1 Brand",'Same Day in Karachi'];
  return (
    <div className="bg-[#1a3a6e] py-3 overflow-hidden">
      <div className="flex whitespace-nowrap animate-marquee">
        {[...items,...items].map((item,i) => (
          <span key={i} className="inline-flex items-center gap-3 text-white text-[11px] font-black uppercase tracking-[0.2em] mr-10">
            <span className="w-1.5 h-1.5 bg-[#e63946] rounded-full flex-shrink-0 animate-pulse"/>{item}
          </span>
        ))}
      </div>
    </div>
  );
};

// ─── TRUST BAR ────────────────────────────────────────────────────────────────
const TrustBar = () => {
  const feats = [
    { icon: <svg className="w-5 h-5" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M5 8h14M5 8a2 2 0 110-4h14a2 2 0 110 4M5 8v10a2 2 0 002 2h10a2 2 0 002-2V8m-9 4h4"/></svg>, title:'Free Delivery', desc:'Orders over PKR 5,000', color:'text-[#1a3a6e] bg-blue-50' },
    { icon: <svg className="w-5 h-5" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z"/></svg>, title:'1-Year Warranty', desc:'On all products', color:'text-[#e63946] bg-red-50' },
    { icon: <svg className="w-5 h-5" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M9 12.75L11.25 15 15 9.75M21 12c0 1.268-.63 2.39-1.593 3.068a3.745 3.745 0 01-1.043 3.296 3.745 3.745 0 01-3.296 1.043A3.745 3.745 0 0112 21c-1.268 0-2.39-.63-3.068-1.593a3.746 3.746 0 01-3.296-1.043 3.745 3.745 0 01-1.043-3.296A3.745 3.745 0 013 12c0-1.268.63-2.39 1.593-3.068a3.745 3.745 0 011.043-3.296 3.746 3.746 0 013.296-1.043A3.746 3.746 0 0112 3c1.268 0 2.39.63 3.068 1.593a3.746 3.746 0 013.296 1.043 3.746 3.746 0 011.043 3.296A3.745 3.745 0 0121 12z"/></svg>, title:'Quality Checked', desc:'Every item inspected', color:'text-green-600 bg-green-50' },
    { icon: <svg className="w-5 h-5" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M18.364 5.636l-3.536 3.536m0 5.656l3.536 3.536M9.172 9.172L5.636 5.636m3.536 9.192l-3.536 3.536M21 12a9 9 0 11-18 0 9 9 0 0118 0zm-5 0a4 4 0 11-8 0 4 4 0 018 0z"/></svg>, title:'24/7 Support', desc:'WhatsApp & call', color:'text-purple-600 bg-purple-50' },
  ];
  return (
    <section className="bg-white border-b border-gray-100">
      <div className="max-w-7xl mx-auto px-4">
        <div className="grid grid-cols-2 lg:grid-cols-4 divide-x divide-gray-100">
          {feats.map(({ icon, title, desc, color }, i) => (
            <FadeUp key={title} delay={i * 0.08}>
              <div className="flex items-center gap-4 px-6 py-6 group hover:bg-gray-50 transition-colors cursor-default">
                <div className={`w-12 h-12 ${color} flex items-center justify-center flex-shrink-0 group-hover:scale-110 transition-transform duration-300 rounded-xl`}>{icon}</div>
                <div><p className="font-bold text-sm text-gray-900">{title}</p><p className="text-xs text-gray-500 mt-0.5">{desc}</p></div>
              </div>
            </FadeUp>
          ))}
        </div>
      </div>
    </section>
  );
};

// ─── BROWSE BY COLLECTION — real admin collections, properly aligned ─────────
const CategoriesSection = ({ collections, loading }) => {
  if (!loading && collections.length === 0) return null;
  const displayCollections = collections.slice(0, 4);

  return (
    <section className="section-pad bg-[#f8f9fc]">
      <div className="max-w-7xl mx-auto px-4">
        <FadeUp className="text-center mb-14">
          <span className="text-[#e63946] text-xs font-black uppercase tracking-[0.3em] block mb-3">Browse By Collection</span>
          <h2 className="font-display text-4xl md:text-5xl font-black text-gray-900">Find Your <span className="text-[#1a3a6e]">Perfect</span> Piece</h2>
          <p className="text-gray-500 mt-3 max-w-lg mx-auto">From cozy living rooms to productive offices — every seating solution in one place.</p>
        </FadeUp>
        {loading ? (
          <div className="grid grid-cols-2 gap-5">
            {Array(4).fill(0).map((_, i) => <div key={i} className="bg-gray-200 animate-pulse aspect-[4/3]" />)}
          </div>
        ) : (
          <div className="grid grid-cols-2 gap-5">
            {displayCollections.map((col, i) => (
              <motion.div key={col.id || col.slug}
                initial={{ opacity: 0, scale: 0.93 }} whileInView={{ opacity: 1, scale: 1 }}
                viewport={{ once: true }} transition={{ delay: i * 0.08, duration: 0.55 }}>
                <Link to={`/collections/${col.slug || col.id}`}
                  className="group relative overflow-hidden block w-full aspect-[4/3] bg-gray-200">
                  <img src={col.image} alt={col.name}
                    className="w-full h-full object-cover transition-all duration-700 group-hover:scale-110"
                    onError={e => e.target.src = `https://via.placeholder.com/400x300?text=${encodeURIComponent(col.name)}`}/>
                  <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/20 to-transparent"/>
                  <div className="absolute inset-0 bg-[#e63946]/0 group-hover:bg-[#e63946]/15 transition-colors duration-500"/>
                  <div className="absolute inset-0 border-2 border-transparent group-hover:border-white/30 transition-all duration-500"/>
                  <div className="absolute bottom-0 left-0 right-0 p-5">
                    <p className="text-white font-black text-lg leading-tight">{col.name}</p>
                    <p className="text-white/0 group-hover:text-white/70 text-xs mt-0.5 transition-all duration-300 transform translate-y-2 group-hover:translate-y-0">Tap to explore →</p>
                  </div>
                </Link>
              </motion.div>
            ))}
          </div>
        )}
      </div>
    </section>
  );
};

// ─── PRODUCT SECTION — supports a horizontal slider mode for "all products" ──
const ProductSection = ({ tag, title, accent, products, loading, bg = 'white', link, linkLabel = 'View All', slider = false }) => {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: '-100px' });
  const dark = bg === 'dark';
  const scrollerRef = useRef(null);

  const scrollBy = (dir) => {
    if (!scrollerRef.current) return;
    scrollerRef.current.scrollBy({ left: dir * 320, behavior: 'smooth' });
  };

  return (
    <section ref={ref} className={`section-pad ${dark ? 'bg-[#0b1f42]' : bg === 'gray' ? 'bg-[#f8f9fc]' : 'bg-white'} relative overflow-hidden`}>
      {dark && <div className="absolute inset-0 diagonal-bg-light opacity-50"/>}
      <div className="relative max-w-7xl mx-auto px-4">
        <div className="flex items-end justify-between mb-12">
          <motion.div initial={{ opacity: 0, y: 30 }} animate={inView ? { opacity: 1, y: 0 } : {}} transition={{ duration: 0.6 }}>
            <span className="text-[#e63946] text-xs font-black uppercase tracking-[0.3em] block mb-2">{tag}</span>
            <h2 className={`font-display text-4xl md:text-5xl font-black ${dark ? 'text-white' : 'text-gray-900'}`}>
              {title}{' '}{accent && <span className={dark ? 'text-[#e63946]' : 'text-[#1a3a6e]'}>{accent}</span>}
            </h2>
          </motion.div>
          <div className="flex items-center gap-3">
            {slider && products.length > 4 && (
              <motion.div initial={{ opacity: 0 }} animate={inView ? { opacity: 1 } : {}} transition={{ delay: 0.2 }} className="hidden sm:flex gap-2">
                <button onClick={() => scrollBy(-1)} className={`w-9 h-9 rounded-full flex items-center justify-center border transition-colors ${dark ? 'border-white/20 text-white/70 hover:text-white hover:border-white/50' : 'border-gray-200 text-gray-500 hover:text-[#1a3a6e] hover:border-[#1a3a6e]'}`}>
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth="2.5" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M15 19l-7-7 7-7"/></svg>
                </button>
                <button onClick={() => scrollBy(1)} className={`w-9 h-9 rounded-full flex items-center justify-center border transition-colors ${dark ? 'border-white/20 text-white/70 hover:text-white hover:border-white/50' : 'border-gray-200 text-gray-500 hover:text-[#1a3a6e] hover:border-[#1a3a6e]'}`}>
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth="2.5" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7"/></svg>
                </button>
              </motion.div>
            )}
            {link && (
              <motion.div initial={{ opacity: 0 }} animate={inView ? { opacity: 1 } : {}} transition={{ delay: 0.2 }}>
                <Link to={link} className={`hidden sm:flex items-center gap-2 text-sm font-bold group hover:gap-3 transition-all ${dark ? 'text-white/70 hover:text-white' : 'text-[#1a3a6e] hover:text-[#e63946]'}`}>
                  {linkLabel}
                  <svg className="w-4 h-4 group-hover:translate-x-1 transition-transform" fill="none" stroke="currentColor" strokeWidth="2.5" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M17 8l4 4m0 0l-4 4m4-4H3"/></svg>
                </Link>
              </motion.div>
            )}
          </div>
        </div>

        {loading ? (
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-5">
            {Array(4).fill(0).map((_, i) => <SkeletonCard key={i} />)}
          </div>
        ) : products.length > 0 ? (
          slider ? (
            <div ref={scrollerRef} className="flex gap-5 overflow-x-auto pb-2 snap-x snap-mandatory scrollbar-hide" style={{ scrollbarWidth: 'none' }}>
              {products.map((p, i) => (
                <motion.div key={p.id} initial={{ opacity: 0, y: 30 }} animate={inView ? { opacity: 1, y: 0 } : {}}
                  transition={{ delay: 0.05 + i * 0.05, duration: 0.5 }}
                  className="flex-shrink-0 w-[230px] sm:w-[260px] snap-start">
                  <ProductCard product={p} index={i} />
                </motion.div>
              ))}
            </div>
          ) : (
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-5">
              {products.map((p, i) => (
                <motion.div key={p.id} initial={{ opacity: 0, y: 30 }} animate={inView ? { opacity: 1, y: 0 } : {}}
                  transition={{ delay: 0.1 + i * 0.1, duration: 0.55, ease: [0.22, 1, 0.36, 1] }}>
                  <ProductCard product={p} index={i} />
                </motion.div>
              ))}
            </div>
          )
        ) : (
          <div className={`text-center py-16 ${dark ? 'text-white/30' : 'text-gray-300'}`}>
            <svg className="w-12 h-12 mx-auto mb-2 opacity-40" fill="none" stroke="currentColor" strokeWidth="1.5" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4"/></svg>
            <p className="text-sm">Products coming soon — Add from admin panel</p>
          </div>
        )}
      </div>
    </section>
  );
};

// ─── PROMO BANNERS ────────────────────────────────────────────────────────────
const PromoSection = () => (
  <section className="section-pad bg-white">
    <div className="max-w-7xl mx-auto px-4">
      <div className="grid md:grid-cols-2 gap-5">
        {[
          { title:'Shop Everything', sub:'Work Smarter, Sit Better', desc:'Explore our full range of ergonomic chairs built to boost productivity.', img:'https://images.unsplash.com/photo-1586023492125-27b2c045efd7?w=700&q=80', link:'/shop', btnText:'Shop Now', overlayFrom:'#1a3a6e', delay:0 },
          { title:'Browse Collections', sub:'Gather Around in Style', desc:'Discover curated collections for every room in your home.', img:'https://images.unsplash.com/photo-1449247709967-d4461a6a6103?w=700&q=80', link:'/collections', btnText:'View Collections', overlayFrom:'#e63946', delay:0.12 }
        ].map(({ title, sub, desc, img, link, btnText, overlayFrom, delay }) => (
          <FadeUp key={title} delay={delay}>
            <div className="group relative overflow-hidden min-h-[380px] flex items-end cursor-pointer">
              <img src={img} alt={title} className="absolute inset-0 w-full h-full object-cover transition-all duration-700 group-hover:scale-105"
                onError={e=>e.target.src=`https://via.placeholder.com/700x380?text=${title}`}/>
              <div className="absolute inset-0 transition-opacity duration-500"
                style={{ background:`linear-gradient(to top, ${overlayFrom}F0 0%, ${overlayFrom}88 45%, transparent 100%)` }}/>
              <div className="relative p-8 w-full">
                <p className="text-white/70 text-xs font-black uppercase tracking-widest mb-1.5">{sub}</p>
                <h3 className="font-display text-3xl font-black text-white mb-2">{title}</h3>
                <p className="text-white/70 text-sm mb-5 leading-relaxed">{desc}</p>
                <Link to={link} className="inline-flex items-center gap-2 bg-white text-gray-900 px-6 py-3 text-sm font-black hover:bg-[#e63946] hover:text-white transition-all duration-300 group/btn">
                  {btnText}
                  <svg className="w-4 h-4 group-hover/btn:translate-x-1 transition-transform" fill="none" stroke="currentColor" strokeWidth="2.5" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M17 8l4 4m0 0l-4 4m4-4H3"/></svg>
                </Link>
              </div>
            </div>
          </FadeUp>
        ))}
      </div>
    </div>
  </section>
);

// ─── FULL-WIDTH BANNER ────────────────────────────────────────────────────────
const FullWidthBanner = () => (
  <section className="relative overflow-hidden min-h-[380px] flex items-center">
    <img src="https://images.unsplash.com/photo-1555041469-a586c61ea9bc?w=1600&q=85" alt="Sale Banner"
      className="absolute inset-0 w-full h-full object-cover"
      onError={e=>e.target.src='https://via.placeholder.com/1600x380?text=SitSmart'}/>
    <div className="absolute inset-0 bg-gradient-to-r from-[#0f2347]/95 via-[#0f2347]/70 to-transparent"/>
    <div className="relative max-w-7xl mx-auto px-4 w-full py-16">
      <FadeLeft>
        <span className="inline-block bg-[#e63946] text-white text-xs font-black uppercase tracking-widest px-3 py-1.5 mb-5">Quality You Can Trust</span>
        <h2 className="font-display text-5xl md:text-6xl font-black text-white mb-4 leading-tight">
          Built To <span className="text-[#e63946]">Last.</span><br/>Priced To Love.
        </h2>
        <p className="text-white/70 text-lg mb-8 max-w-md">Premium quality furniture at honest prices — shop the full range today.</p>
        <div className="flex gap-4">
          <Link to="/shop" className="inline-flex items-center gap-2 bg-[#e63946] text-white px-8 py-4 font-black hover:bg-white hover:text-[#e63946] transition-all duration-300">
            Shop Now
            <svg className="w-5 h-5" fill="none" stroke="currentColor" strokeWidth="2.5" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M17 8l4 4m0 0l-4 4m4-4H3"/></svg>
          </Link>
          <Link to="/collections" className="inline-flex items-center gap-2 border-2 border-white/40 text-white px-8 py-4 font-black hover:border-white hover:bg-white/10 transition-all duration-300">
            View Collections
          </Link>
        </div>
      </FadeLeft>
    </div>
  </section>
);

// ─── OUR STORY (LIGHT) ────────────────────────────────────────────────────────
const OurStorySection = () => (
  <section className="section-pad bg-white">
    <div className="max-w-7xl mx-auto px-4">
      <div className="grid lg:grid-cols-2 gap-16 items-center">
        <FadeLeft>
          <div className="relative">
            <div className="grid grid-cols-2 gap-4">
              <img src="https://images.unsplash.com/photo-1555041469-a586c61ea9bc?w=400&q=80" alt="Quality"
                className="w-full h-64 object-cover shadow-lg"
                onError={e=>e.target.src='https://via.placeholder.com/400x256?text=Quality'}/>
              <img src="https://images.unsplash.com/photo-1586023492125-27b2c045efd7?w=400&q=80" alt="Design"
                className="w-full h-64 object-cover mt-8 shadow-lg"
                onError={e=>e.target.src='https://via.placeholder.com/400x256?text=Design'}/>
            </div>
            <motion.div animate={{ y:[0,-8,0] }} transition={{ duration:3, repeat:Infinity }}
              className="absolute -bottom-6 left-8 bg-[#1a3a6e] text-white p-5 shadow-xl">
              <p className="font-black text-3xl">10+</p>
              <p className="text-white/70 text-xs uppercase tracking-wider mt-0.5">Years of Excellence</p>
            </motion.div>
          </div>
        </FadeLeft>
        <FadeRight>
          <span className="text-[#e63946] text-xs font-black uppercase tracking-[0.3em] block mb-4">Our Story</span>
          <h2 className="font-display text-4xl md:text-5xl font-black text-gray-900 mb-6 leading-tight">
            Crafted For Pakistani Homes & Offices
          </h2>
          <p className="text-gray-500 leading-relaxed mb-6">
            For over a decade, SitSmart has been Pakistan's trusted name in smart seating. We combine international quality standards with a deep understanding of local needs.
          </p>
          <p className="text-gray-500 leading-relaxed mb-8">
            From Karachi to Khyber — every chair we make is built to last, designed to impress, and priced for every Pakistani home.
          </p>
          <div className="grid grid-cols-2 gap-4 mb-8">
            {[
              { title:'Premium Materials', desc:'UV-resistant, weather-proof plastic that lasts years.' },
              { title:'Safety Certified', desc:'All baby furniture meets international safety standards.' },
              { title:'Nationwide Delivery', desc:'Fast delivery to 100+ cities across Pakistan.' },
              { title:'After-Sales Support', desc:'1-year warranty with dedicated care team.' },
            ].map(({ title, desc }) => (
              <div key={title} className="flex items-start gap-2.5">
                <div className="w-5 h-5 bg-[#e63946] flex items-center justify-center flex-shrink-0 mt-0.5 rounded-sm">
                  <svg className="w-3 h-3 text-white" fill="none" stroke="currentColor" strokeWidth="3" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7"/></svg>
                </div>
                <div>
                  <p className="font-bold text-gray-900 text-sm">{title}</p>
                  <p className="text-gray-400 text-xs mt-0.5">{desc}</p>
                </div>
              </div>
            ))}
          </div>
          <div className="flex gap-4">
            <Link to="/about" className="btn-primary text-sm py-3">Our Full Story</Link>
            <Link to="/contact" className="btn-outline text-sm py-3">Contact Us</Link>
          </div>
        </FadeRight>
      </div>
    </div>
  </section>
);

// ─── TESTIMONIALS ─────────────────────────────────────────────────────────────
const TestimonialsSection = () => {
  const [active, setActive] = useState(0);
  const reviews = [
    { name:'Ahmed Khan', city:'Karachi', text:'Ordered 20 chairs for my office — best decision ever. Premium quality, fast delivery, and excellent support. SitSmart is our go-to brand.', rating:5, avatar:'AK', product:'Office Chairs' },
    { name:'Sara Ali', city:'Lahore', text:'The baby high chair is incredibly safe and my toddler loves it! Build quality is superb. Will definitely order again. Fast delivery to Lahore.', rating:5, avatar:'SA', product:'Baby High Chair' },
    { name:'Imran Malik', city:'Islamabad', text:'Ergonomic chairs transformed our workspace. Team productivity is up and everyone loves sitting in them. Great investment, worth every rupee.', rating:5, avatar:'IM', product:'Ergonomic Chair' },
    { name:'Fatima Zahra', city:'Rawalpindi', text:'Dining set looks absolutely beautiful in my home. Exactly as shown in photos, maybe even better. Super quick delivery and zero damage.', rating:5, avatar:'FZ', product:'Dining Table Set' },
  ];
  useEffect(() => {
    const t = setInterval(() => setActive(a=>(a+1)%reviews.length), 5000);
    return () => clearInterval(t);
  }, []);
  return (
    <section className="section-pad bg-[#f8f9fc] relative overflow-hidden">
      <div className="absolute top-0 right-0 w-96 h-96 bg-[#1a3a6e]/5 rounded-full translate-x-1/2 -translate-y-1/2 pointer-events-none"/>
      <div className="absolute bottom-0 left-0 w-64 h-64 bg-[#e63946]/5 rounded-full -translate-x-1/2 translate-y-1/2 pointer-events-none"/>
      <div className="relative max-w-5xl mx-auto px-4">
        <FadeUp className="text-center mb-14">
          <span className="text-[#e63946] text-xs font-black uppercase tracking-[0.3em] block mb-3">Customer Love</span>
          <h2 className="font-display text-4xl md:text-5xl font-black text-gray-900">Loved By <span className="text-[#1a3a6e]">50,000+ Families</span></h2>
          <p className="text-gray-500 mt-3 max-w-lg mx-auto">Real customers, real experiences across Pakistan</p>
        </FadeUp>
        <div className="relative mb-6">
          <AnimatePresence mode="wait">
            <motion.div key={active} initial={{ opacity:0, y:24, scale:0.97 }} animate={{ opacity:1, y:0, scale:1 }}
              exit={{ opacity:0, y:-16 }} transition={{ duration:0.5 }}
              className="bg-white p-6 sm:p-10 md:p-14 text-center relative shadow-luxury">
              <div className="font-serif text-[120px] leading-none text-[#e63946]/8 absolute -top-4 left-8 font-black select-none text-[#e63946]" style={{opacity:0.08}}>"</div>
              <div className="flex justify-center gap-1 mb-6">
                {Array(reviews[active].rating).fill(0).map((_,i)=>(
                  <svg key={i} className="w-5 h-5 text-amber-400" fill="currentColor" viewBox="0 0 24 24"><path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z"/></svg>
                ))}
              </div>
              <p className="text-gray-700 text-xl md:text-2xl leading-relaxed mb-8 font-medium max-w-2xl mx-auto relative z-10 italic">"{reviews[active].text}"</p>
              <div className="flex items-center justify-center gap-4">
                <div className="w-12 h-12 bg-gradient-to-br from-[#1a3a6e] to-[#2a5298] rounded-full flex items-center justify-center font-black text-white">{reviews[active].avatar}</div>
                <div className="text-left">
                  <p className="font-bold text-gray-900">{reviews[active].name}</p>
                  <p className="text-gray-400 text-sm">{reviews[active].city} · {reviews[active].product}</p>
                </div>
              </div>
            </motion.div>
          </AnimatePresence>
        </div>
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-3">
          {reviews.map((r,i)=>(
            <button key={r.name} onClick={()=>setActive(i)}
              className={`text-left p-4 border-2 transition-all duration-300 bg-white hover:shadow-md ${i===active?'border-[#1a3a6e] shadow-md':'border-gray-100 hover:border-gray-200'}`}>
              <div className="flex gap-0.5 mb-2">
                {Array(r.rating).fill(0).map((_,j)=><svg key={j} className="w-3 h-3 text-amber-400" fill="currentColor" viewBox="0 0 24 24"><path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z"/></svg>)}
              </div>
              <p className="text-gray-600 text-xs leading-relaxed line-clamp-2 mb-2">"{r.text}"</p>
              <p className="text-gray-400 text-xs font-semibold">{r.name} · {r.city}</p>
            </button>
          ))}
        </div>
      </div>
    </section>
  );
};

// ─── MAIN ─────────────────────────────────────────────────────────────────────
const HomePage = () => {
  const [allProducts, setAllProducts] = useState([]);
  const [collections, setCollections] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    Promise.all([getProducts({ limitCount: 200 }), getCollections()])
      .then(([prods, cols]) => { setAllProducts(prods); setCollections(cols); })
      .catch(console.error).finally(() => setLoading(false));
  }, []);

  // Hero pulls from real products — new arrivals first, falling back to anything available
  const heroProducts = [...allProducts]
    .filter(p => p.images?.length > 0)
    .sort((a, b) => (b.isNew === true) - (a.isNew === true) || (b.createdAt || 0) - (a.createdAt || 0))
    .slice(0, 5);

  // Newest-first list for the "New Arrivals" slider — every product, not a fixed category
  const newest = [...allProducts].sort((a, b) => (b.createdAt || 0) - (a.createdAt || 0));

  // Only show collections that actually have 4+ products behind them, sorted by admin order
  const sortedCollections = [...collections].sort((a, b) => (a.order || 0) - (b.order || 0));
  const collectionsWithCounts = sortedCollections
    .map(col => ({ ...col, products: allProducts.filter(p => p.collectionId === col.id) }))
    .filter(col => col.products.length >= 4);

  const featuredCollectionSections = collectionsWithCounts.slice(0, 3);

  return (
    <>
      <SEO title="Smart Seating For Every Home"
        description="Pakistan's #1 smart seating brand. Premium plastic furniture, baby chairs, office chairs & dining tables. Free delivery on orders over PKR 5,000."
        keywords="plastic chairs pakistan, office chairs karachi, baby chairs, furniture online pakistan, sitsmart"/>
      <motion.div initial={{ opacity:0 }} animate={{ opacity:1 }} transition={{ duration:0.4 }}>
        <Hero heroProducts={heroProducts} loading={loading}/>
        <MarqueeBar/>
        <TrustBar/>
        <CategoriesSection collections={sortedCollections} loading={loading}/>

        {/* New Arrivals — every product, scrollable slider */}
        <ProductSection
          tag="Just Dropped" title="New" accent="Arrivals"
          products={newest} loading={loading} bg="white" slider
          link="/shop" linkLabel="View All Products"
        />

        <PromoSection/>

        {/* Up to 3 collection-based sections, only for collections with 4+ products */}
        {featuredCollectionSections[0] && (
          <ProductSection
            tag="Collection" title={featuredCollectionSections[0].name} accent=""
            products={featuredCollectionSections[0].products.slice(0, 8)}
            loading={loading} bg="gray"
            link={featuredCollectionSections[0].products.length > 4 ? `/collections/${featuredCollectionSections[0].slug || featuredCollectionSections[0].id}` : undefined}
            linkLabel="View All"
          />
        )}

        <FullWidthBanner/>

        {featuredCollectionSections[1] && (
          <ProductSection
            tag="Collection" title={featuredCollectionSections[1].name} accent=""
            products={featuredCollectionSections[1].products.slice(0, 8)}
            loading={loading} bg="white"
            link={featuredCollectionSections[1].products.length > 4 ? `/collections/${featuredCollectionSections[1].slug || featuredCollectionSections[1].id}` : undefined}
            linkLabel="View All"
          />
        )}

        <OurStorySection/>

        {featuredCollectionSections[2] && (
          <ProductSection
            tag="Collection" title={featuredCollectionSections[2].name} accent=""
            products={featuredCollectionSections[2].products.slice(0, 8)}
            loading={loading} bg="gray"
            link={featuredCollectionSections[2].products.length > 4 ? `/collections/${featuredCollectionSections[2].slug || featuredCollectionSections[2].id}` : undefined}
            linkLabel="View All"
          />
        )}

        <TestimonialsSection/>
        <ContactCTA/>
      </motion.div>
    </>
  );
};
export default HomePage;
